import { HEROES, Slot, type HeroId, type Vec3 } from "@rivalwatch/shared";
import { HERO_VOICE_SCRIPTS, type VoiceLine, type VoiceScript, type VoiceTrigger } from "../heroes/voices.ts";
import { audio, type AudioEngine, type Placement } from "./engine.ts";

export type VoiceRel = "self" | "friend" | "foe";

interface VoicePlay {
  hero: HeroId;
  trigger: VoiceTrigger;
  speaker?: number;
  rel?: VoiceRel;
  pos?: Vec3;
  form?: number;
  chance?: number;
}

interface ActiveLine {
  speaker: number;
  until: number;
  priority: number;
}

const ULT_TRIGGERS = new Set<VoiceTrigger>(["ult_ally", "ult_enemy"]);
const ABILITY_TRIGGERS = new Set<VoiceTrigger>(["secondary", "ability1", "ability2", "ability3"]);

const cooldownOf = (trigger: VoiceTrigger) => (ABILITY_TRIGGERS.has(trigger) ? 6 : trigger === "kill" ? 8 : trigger === "low_hp" ? 25 : trigger === "healed" ? 30 : 0);
const priorityOf = (trigger: VoiceTrigger) => (trigger === "ult_enemy" ? 9 : trigger === "ult_ally" ? 8 : trigger === "kill" || trigger === "multikill" || trigger === "revived" ? 7 : ABILITY_TRIGGERS.has(trigger) ? 5 : 4);
const up = (p: Vec3): Vec3 => ({ x: p.x, y: p.y + 1.35, z: p.z });

function triggerForSlot(slot: number): VoiceTrigger | undefined {
  switch (slot) {
    case Slot.Secondary:
      return "secondary";
    case Slot.Ability1:
      return "ability1";
    case Slot.Ability2:
      return "ability2";
    case Slot.Ability3:
      return "ability3";
    default:
      return undefined;
  }
}

function pick(script: VoiceScript, trigger: VoiceTrigger, form?: number): VoiceLine | undefined {
  const all = script.lines[trigger] ?? [];
  if (!all.length) return undefined;
  const eligible = form === undefined ? all.filter((l) => l.form === undefined) : all.filter((l) => l.form === undefined || l.form === form);
  const lines = eligible.length ? eligible : all;
  return lines[Math.floor(Math.random() * lines.length)];
}

function placement(trigger: VoiceTrigger, rel: VoiceRel, pos: Vec3 | undefined, priority: number): Placement | undefined {
  if (rel === "self" || !pos) {
    const gain = ULT_TRIGGERS.has(trigger) ? (trigger === "ult_enemy" ? 1 : 0.72) : ABILITY_TRIGGERS.has(trigger) ? 0.42 : 0.48;
    return { bus: "voice", gain, priority };
  }
  if (trigger === "ult_enemy") return { bus: "voice", at: up(pos), gain: 1, ref: 40, range: 220, priority, wet: 0.08 };
  if (trigger === "ult_ally") return { bus: "voice", at: up(pos), gain: 0.68, ref: 18, range: 140, priority, wet: 0.08 };
  return { bus: "voice", at: up(pos), gain: rel === "friend" ? 0.28 : 0.34, ref: 3, range: 25, priority, wet: 0.04 };
}

export class VoiceDirector {
  private readonly last = new Map<string, number>();
  private readonly preloaded = new Set<string>();
  private active: ActiveLine[] = [];
  private announcerBusy = () => false;

  constructor(private readonly engine: AudioEngine = audio) {}

  bindAnnouncer(isBusy: () => boolean) {
    this.announcerBusy = isBusy;
  }

  preloadMatch(heroes: Iterable<HeroId>) {
    for (const hero of heroes) {
      for (const trigger of ["ult_ally", "ult_enemy"] as const) {
        for (const line of HERO_VOICE_SCRIPTS[hero].lines[trigger] ?? []) {
          const key = `${hero}:${line.id}`;
          if (this.preloaded.has(key)) continue;
          this.preloaded.add(key);
          void this.engine.buffer(this.url(hero, line), true);
        }
      }
    }
  }

  playCast(hero: HeroId, owner: number, slot: number, rel: VoiceRel, pos?: Vec3, form?: number): boolean {
    if (slot === Slot.Ultimate) return this.play({ hero, trigger: rel === "foe" ? "ult_enemy" : "ult_ally", speaker: owner, rel, pos, form });
    const trigger = triggerForSlot(slot);
    return trigger ? this.play({ hero, trigger, speaker: owner, rel, pos, form }) : false;
  }

  play(req: VoicePlay): boolean {
    if (req.chance !== undefined && Math.random() > req.chance) return false;
    const script = HERO_VOICE_SCRIPTS[req.hero];
    const line = pick(script, req.trigger, req.form);
    if (!line) return false;
    const now = performance.now() / 1000;
    const priority = priorityOf(req.trigger);
    const speaker = req.speaker ?? -1;
    const cd = cooldownOf(req.trigger);
    const lastKey = `${speaker}:${req.hero}:${req.trigger}:${line.id}`;
    if (cd > 0 && now - (this.last.get(lastKey) ?? -1e9) < cd) return false;
    this.active = this.active.filter((a) => a.until > now);
    if (this.active.some((a) => a.speaker === speaker && a.until > now)) return false;
    if (this.announcerBusy() && priority < 8) return false;
    if (this.active.length >= 2 && priority < 9) return false;
    const p = placement(req.trigger, req.rel ?? "self", req.pos, priority);
    if (!p) return false;
    this.last.set(lastKey, now);
    void this.engine.sample(p, this.url(req.hero, line), 1, true).then((dur) => {
      if (dur <= 0) return;
      this.active.push({ speaker, until: performance.now() / 1000 + dur + 0.1, priority });
      if (ULT_TRIGGERS.has(req.trigger)) this.engine.duck(req.trigger === "ult_enemy" ? 6 : 4, dur + 0.1);
    });
    return true;
  }

  private url(hero: HeroId, line: VoiceLine) {
    return `/assets/audio/heroes/${HEROES[hero].slug}/${line.id}.mp3`;
  }
}

const selectDirector = new VoiceDirector(audio);

export function playSelectLine(hero: HeroId) {
  audio.resume();
  selectDirector.play({ hero, trigger: "select", rel: "self" });
}
